import { ReactNode } from "react";
import { AlertTriangle } from "lucide-react";

import { DocumentEntity, DocumentRelation } from "../../DocumentAnalysisAPI";
import { Issue } from "./ontologyValidation";

import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

/**
 * How the catalogue reads in the table.
 *
 * The colours carry meaning and only that: a category tells the reader which part of the business
 * a document belongs to, a sharpness says how firmly a back-reference identifies its target, and
 * red means the entry disagrees with the ontology. Nothing here is decorative — a table of forty
 * chips in six random colours is harder to read than plain text.
 */

/** One colour per category, stable so the eye learns them. */
const CATEGORY_TONE: Record<string, string> = {
  Miete: "bg-sky-100 text-sky-900 dark:bg-sky-950 dark:text-sky-200",
  Betrieb: "bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200",
  Eigentum: "bg-violet-100 text-violet-900 dark:bg-violet-950 dark:text-violet-200",
  Bau: "bg-orange-100 text-orange-900 dark:bg-orange-950 dark:text-orange-200",
  Finanzen: "bg-emerald-100 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-200",
  ESG: "bg-teal-100 text-teal-900 dark:bg-teal-950 dark:text-teal-200",
};

export function CategoryBadge({ category }: { category: string }) {
  return (
    <span
      className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ${
        CATEGORY_TONE[category] ?? "bg-muted text-muted-foreground"
      }`}
    >
      {category}
    </span>
  );
}

/** How firmly a back-reference identifies what it points at. */
const SHARPNESS_TONE: Record<string, string> = {
  amtlich: "text-emerald-700 dark:text-emerald-300",
  formal: "text-amber-700 dark:text-amber-300",
  frei: "text-muted-foreground",
};

export function SharpnessMark({ sharpness }: { sharpness?: string }) {
  if (!sharpness) return null;

  return <span className={`text-xs ${SHARPNESS_TONE[sharpness] ?? ""}`}>[{sharpness}]</span>;
}

/** An operator chip: what the subtype does to the state of the agreement. */
export function OperatorChip({ operator, exTunc, note }: { operator?: string; exTunc?: boolean; note?: string }) {
  if (!operator) return null;

  const chip = (
    <Badge variant="outline" className="gap-1 font-mono text-[11px]">
      {operator}
      {/* Ex tunc: the change applies from an earlier date than the document carries. Worth its own
          mark, because it decides which state a date falls into. */}
      {exTunc ? <span title="ex tunc">↺</span> : null}
    </Badge>
  );

  if (!note) return chip;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span>{chip}</span>
        </TooltipTrigger>
        <TooltipContent>{note}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/** A reference code and whether anyone has checked it against gif/GEFMA yet. */
export function RefCodeMark({ source, code, status }: { source: string; code: string | null; status: string }) {
  return (
    <span className="inline-flex items-center gap-1 text-xs">
      <span
        aria-hidden
        className={`inline-block h-2 w-2 rounded-full ${
          status === "verified" ? "bg-emerald-500" : "bg-muted-foreground/40"
        }`}
      />
      <span className="font-mono">
        {source}:{code ?? "—"}
      </span>
    </span>
  );
}

/**
 * A term with the class it maps to.
 *
 * Three states, and the difference matters: mapped (plain), unmapped (grey, "ohne Klasse" — work
 * waiting, not a defect), and wrong (red with the reason on hover).
 */
export function EntityChip({ entity, issue }: { entity: DocumentEntity; issue?: Issue }) {
  const unmapped = !entity.className;

  const chip = (
    <span
      className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs ${
        issue
          ? "border-destructive text-destructive"
          : unmapped
            ? "border-dashed border-muted-foreground/40 text-muted-foreground"
            : "border-border"
      }`}
    >
      {issue ? <AlertTriangle className="h-3 w-3" aria-hidden /> : null}
      <span>{entity.term}</span>
      <span className="text-muted-foreground">→ {entity.className ?? "ohne Klasse"}</span>
    </span>
  );

  return issue ? withReason(chip, issue.message) : chip;
}

export function RelationLine({ relation, issue }: { relation: DocumentRelation; issue?: Issue }) {
  const line = (
    <span className={`inline-flex items-center gap-1 text-xs ${issue ? "text-destructive" : ""}`}>
      {issue ? <AlertTriangle className="h-3 w-3" aria-hidden /> : null}
      <span>{relation.sourceTerm}</span>
      <span className="font-mono">—{relation.name}→</span>
      <span>{relation.targetTerm}</span>
    </span>
  );

  return issue ? withReason(line, issue.message) : line;
}

/** A role reference that may no longer point anywhere. */
export function RoleReference({ target, suffix, issue }: { target: string; suffix?: ReactNode; issue?: Issue }) {
  const line = (
    <span className={`inline-flex items-center gap-1 text-xs ${issue ? "text-destructive" : ""}`}>
      {issue ? <AlertTriangle className="h-3 w-3" aria-hidden /> : null}
      <span>{target}</span>
      {suffix}
    </span>
  );

  return issue ? withReason(line, issue.message) : line;
}

const withReason = (element: ReactNode, reason: string) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <span>{element}</span>
      </TooltipTrigger>
      <TooltipContent>{reason}</TooltipContent>
    </Tooltip>
  </TooltipProvider>
);
